/**
 * Diálogo "Cancelar egreso": anula en el sistema la traza de un despacho y
 * decide qué pasa con las unidades según dónde están (ver
 * `cancelarEgresoDespacho` en el back):
 *
 *   - `retrazar`: siguen en el depósito y el remito se vuelve a trazar.
 *   - `liberar`: siguen en el depósito y el remito no sale.
 *   - `en_transito`: salieron y vuelven (el destino no confirmó en ANMAT).
 *
 * NO cancela en ANMAT: el operador confirma que ya canceló ahí las
 * transacciones (ANMAT cancela por transacción, así que se anula la traza
 * entera). Si el destino ya confirmó la recepción, no corresponde cancelar:
 * la vuelta es una devolución que entra por Recepción.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DestinoAnulacionEgreso } from '@/api/endpoints/comprobantes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Códigos de transacción ANMAT de la traza (a cancelar en ANMAT). */
  transacciones: readonly string[];
  /** Número AFIP del remito, para recordar anularlo en el ERP. */
  remito: string;
  pending: boolean;
  /** false con la tarea cerrada en Plataforma: el remito no se puede volver a trazar. */
  permitirRetrazar?: boolean;
  onConfirmar: (destino: DestinoAnulacionEgreso) => void;
}

const OPCIONES: ReadonlyArray<{
  destino: DestinoAnulacionEgreso;
  titulo: string;
  detalle: string;
}> = [
  {
    destino: 'retrazar',
    titulo: 'Están en el depósito y se vuelve a trazar este remito',
    detalle:
      'Quedan pickeadas para este remito. El remito vuelve a Pendientes: podés corregir el pickeo y trazarlo de nuevo.',
  },
  {
    destino: 'liberar',
    titulo: 'Están en el depósito y el remito no sale',
    detalle:
      'Quedan disponibles para otro remito y el remito queda anulado. El remito en el ERP se anula a mano.',
  },
  {
    destino: 'en_transito',
    titulo: 'Salieron del depósito y vuelven',
    detalle:
      'El destino todavía no confirmó la recepción en ANMAT. Quedan no disponibles hasta confirmar que llegaron de vuelta, y el remito queda anulado.',
  },
];

export function CancelarEgresoDialog({
  open,
  onOpenChange,
  transacciones,
  remito,
  pending,
  permitirRetrazar = true,
  onConfirmar,
}: Props) {
  const [destino, setDestino] = useState<DestinoAnulacionEgreso | null>(null);
  const [confirmaAnmat, setConfirmaAnmat] = useState(false);
  const codigos = Array.from(new Set(transacciones));

  // Cada apertura arranca sin elegir nada.
  useEffect(() => {
    if (open) {
      setDestino(null);
      setConfirmaAnmat(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Cancelar egreso</DialogTitle>
          <DialogDescription className="text-xs">
            Anula la traza <b>solo en el sistema</b>. <b>No cancela en ANMAT</b>: primero cancelá
            las transacciones en la plataforma de ANMAT.
          </DialogDescription>
        </DialogHeader>

        {codigos.length > 0 && (
          <div className="rounded-md border border-gray-200 p-2">
            <div className="mb-1 text-[11px] font-semibold uppercase text-gray-600">
              Transacciones a cancelar en ANMAT
            </div>
            <div className="flex flex-wrap gap-1">
              {codigos.map((c) => (
                <span key={c} className="badge badge-blue font-mono text-[11px]">
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        <fieldset className="space-y-2" disabled={pending}>
          <legend className="mb-1 text-xs font-semibold text-everex-azul">
            ¿Dónde están las unidades?
          </legend>
          {OPCIONES.map((o) => {
            const bloqueada = o.destino === 'retrazar' && !permitirRetrazar;
            return (
              <label
                key={o.destino}
                className={cn(
                  'flex gap-2 rounded-md border p-2 text-xs transition',
                  bloqueada ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  destino === o.destino
                    ? 'border-everex-naranja bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <input
                  type="radio"
                  name="destino-anulacion"
                  className="mt-0.5"
                  checked={destino === o.destino}
                  disabled={bloqueada}
                  onChange={() => setDestino(o.destino)}
                />
                <span>
                  <b>{o.titulo}</b>
                  <span className="block text-gray-600">
                    {bloqueada
                      ? 'No disponible: la tarea ya se cerró en Plataforma y el remito no se puede volver a trazar.'
                      : o.detalle}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          Si el destino <b>ya confirmó la recepción</b> en ANMAT, el egreso no se puede cancelar: la
          vuelta de las unidades es una devolución que el destino traza hacia Everex y entra por
          <b> Recepción</b>.
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-xs">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmaAnmat}
            disabled={pending}
            onChange={(e) => setConfirmaAnmat(e.target.checked)}
          />
          <span>
            Ya cancelé en ANMAT{' '}
            {codigos.length > 0 ? (
              <>
                las transacciones <b className="font-mono">{codigos.join(', ')}</b>
              </>
            ) : (
              'las transacciones de esta traza'
            )}
            {destino === 'liberar' && (
              <>
                {' '}y voy a anular el remito <b className="font-mono">{remito}</b> en el ERP
              </>
            )}
            .
          </span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Volver
          </Button>
          <Button
            onClick={() => destino && onConfirmar(destino)}
            disabled={pending || !destino || !confirmaAnmat}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Cancelar egreso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
